import { ReportFinding, RunReportContext } from './types';
import { RunSnapshot, tidyNumbers } from './runs';
import { runChecks } from './checks';
import { VerdictLabels, finding } from './verdict';

/**
 * What the general procedure test report judges itself, beside its blocks —
 * the run's own result — and what its document prints about the run: its
 * title, its counts, its document control. Part of the SDK, so a report
 * started from the general one (a bundle's general starter) reads the run the
 * same way, and can keep or replace each piece.
 *
 * PASS for a completed, passed run with no failed step or validation; FAIL
 * for a failed run, step or validation; otherwise incomplete, labelled with
 * the run's state.
 */

/**
 * An uploaded run's state is always CREATED — desktop uploads a run once it
 * is over — so its outcome is its status: in progress while that is PENDING
 * (or where a state says it is still running), complete otherwise.
 */
export function runCompleted(run: Pick<RunSnapshot, 'status' | 'state'>): boolean {
  const status = (run.status ?? '').toUpperCase();
  const state = (run.state ?? '').toUpperCase();
  return status !== '' && status !== 'PENDING' && state !== 'RUNNING' && state !== 'INITIALIZED';
}

export function runFindings(run: RunSnapshot): ReportFinding[] {
  const findings: ReportFinding[] = [];
  const status = (run.status ?? '').toUpperCase();
  const completed = runCompleted(run);
  // a run still in progress has no result to report; it gets the in-progress finding below instead
  if (status !== 'PENDING') {
    const message = status ? `The run was recorded ${status}${run.signedBy ? ` and signed by ${run.signedBy}` : ''}.` : 'The run records no result.';
    findings.push(status === 'FAILED' ? finding.defect('run-status', message) : status === 'PASSED' ? finding.pass('run-status', message) : finding.gap('run-status', message));
  }
  if (!completed) findings.push(finding.gap('run-completed', 'The run is still in progress: it has no result yet.'));
  // a step, or a check, that failed the same way more than once is one finding with a count; each failure still prints at its step
  for (const steps of groupedBy(run.steps.filter((x) => (x.status ?? '').toUpperCase() === 'FAILED'), (s) => s.title)) {
    const [s] = steps;
    findings.push(finding.defect(`step-${s.id}`, steps.length === 1 ? `Step failed: ${s.title}` : `Step failed ${steps.length} times: ${s.title}`));
  }
  const validations = run.steps.flatMap((s) => (s.validations ?? []).map((v, i) => ({ step: s, v, i })));
  const failed = validations.filter(({ v }) => !v.passed);
  for (const group of groupedBy(failed, ({ v }) => v.title || 'Validation')) {
    const [{ step, v, i }] = group;
    const title = v.title || 'Validation';
    if (group.length === 1) {
      findings.push(finding.defect(`validation-${step.id}-${i}`, `${title} (${step.title}): ${tidyNumbers(v.message) || 'failed'}`));
      continue;
    }
    const steps = new Set(group.map((g) => g.step.title));
    const where = steps.size === 1 ? step.title : `in ${steps.size} different steps`;
    findings.push(finding.defect(`validation-${step.id}-${i}`, `${title} failed ${group.length} times (${where}). The first: ${tidyNumbers(v.message) || 'failed'}`));
  }
  if (validations.length && failed.length === 0) findings.push(finding.pass('validations', `All ${validations.length} recorded validation${validations.length === 1 ? '' : 's'} passed.`));
  return findings;
}

/** Items grouped by a name, ignoring case and spacing, in the order each name first appears. */
function groupedBy<T>(items: T[], nameOf: (item: T) => string): T[][] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = nameOf(item).trim().toLowerCase().replace(/\s+/g, ' ');
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  return Array.from(groups.values());
}

/**
 * How the verdict reads for this run: an unfinished run is NOT COMPLETE, an
 * aborted one says so; a run that passed or failed but leaves something
 * unjudged (a required field, a value with no limit) is INCOMPLETE.
 */
export function runLabels(run: RunSnapshot): VerdictLabels {
  const status = (run.status ?? '').toUpperCase();
  return { pass: 'PASS', defect: 'FAIL', gap: !runCompleted(run) ? 'NOT COMPLETE' : !status ? 'NOT RUN' : status === 'PASSED' || status === 'FAILED' ? 'INCOMPLETE' : status };
}

export interface RunCounts {
  steps: number;
  failedSteps: number;
  /** validations and yes/no results, as the checks table lists them */
  checks: number;
  failedValidations: number;
  /** yes/no results recorded No */
  noes: number;
}

export function runCounts(run: RunSnapshot): RunCounts {
  const checks = runChecks(run);
  return {
    steps: run.steps.length,
    failedSteps: run.steps.filter((s) => (s.status ?? '').toUpperCase() === 'FAILED').length,
    checks: checks.length,
    failedValidations: checks.filter((c) => c.source === 'validation' && !c.passed).length,
    noes: checks.filter((c) => c.source === 'flag' && !c.passed).length,
  };
}

/** Document control: what the report template says about the test, and what the run says about itself. */
export function runDocControl(context: RunReportContext): Array<{ label: string; value: string }> {
  const run = context.run;
  const template = context.reportTemplate ?? null;
  const plan = [template?.testPlanNumber, template?.testPlanRevision ? `Rev ${template.testPlanRevision}` : ''].filter(Boolean).join(' ');
  return [
    { label: 'Requirement', value: template?.requirement ?? '' },
    { label: 'Test method', value: template?.testMethod ?? '' },
    { label: 'Test plan', value: plan },
    { label: 'Procedure', value: run.sourceTitle ?? '' },
    { label: 'Run', value: run.title },
    { label: 'Completed', value: runCompleted(run) && run.completedAt ? run.completedAt.slice(0, 10) : '' },
    { label: 'Signed by', value: run.signedBy ?? '' },
    { label: 'Report template author', value: template?.reportCreator ?? '' },
  ].filter((d) => d.value);
}

export function reportTitle(context: RunReportContext): string {
  return context.reportTemplate?.title || `Report for ${context.run.sourceTitle || context.run.title}`;
}

export function requirementLine(context: RunReportContext): string {
  const template = context.reportTemplate;
  return [template?.requirement, template?.testMethod].filter(Boolean).join(' · ');
}
